import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, Alert } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { dealersApi } from '../../../api/dealers.api';
import { evcpdProductsApi } from '../../../api/evcpdProducts.api';
import type { CreateInstallationInput } from '../../../api/installations.api';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateInstallationInput) => Promise<void>;
  error?: string | null;
}

const PRIORITIES = ['low', 'medium', 'high', 'critical'];

export function InstallationFormDialog({ open, onClose, onSubmit, error }: Props) {
  const [dealerId, setDealerId] = useState<number | ''>('');
  const [productId, setProductId] = useState<number | ''>('');
  const [siteId, setSiteId] = useState('');
  const [priority, setPriority] = useState('medium');
  const [remarks, setRemarks] = useState('');

  const dealers = useQuery({ queryKey: ['dealers', 'inst-picker'], queryFn: () => dealersApi.list({ status: 'approved', limit: 100 }), enabled: open });
  const products = useQuery({ queryKey: ['evcpd-products', 'inst-picker'], queryFn: () => evcpdProductsApi.list({ status: 'active', limit: 200 }), enabled: open });

  useEffect(() => {
    if (!open) { setDealerId(''); setProductId(''); setSiteId(''); setPriority('medium'); setRemarks(''); }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New Installation Request</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField select label="Dealer" value={dealerId} onChange={(e) => setDealerId(Number(e.target.value))} required fullWidth>
            {(dealers.data?.data ?? []).map((d) => <MenuItem key={d.id} value={d.id}>{d.dealerName} ({d.dealerCode})</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField select label="Product" value={productId} onChange={(e) => setProductId(Number(e.target.value))} fullWidth>
              <MenuItem value="">—</MenuItem>
              {(products.data?.data ?? []).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
            <TextField select label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)} fullWidth>
              {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField label="Site ID (optional)" type="number" value={siteId} onChange={(e) => setSiteId(e.target.value)} fullWidth />
          <TextField label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!dealerId}
          onClick={() => onSubmit({ dealerId: Number(dealerId), productId: productId ? Number(productId) : undefined, siteId: siteId ? Number(siteId) : undefined, priority, remarks: remarks || undefined })}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
